import React, { useState, useEffect, useRef } from 'react';
import styles from '../../styles/LocationOverview.module.css';
import Filter from '../../components/forms/filters';
import PageTitle from '../../components/ui/page-title';
import { PageTitleHeader } from '../../typescript/component';
import { LocationRes, LocationsOverviewPage, TaxonomyRes } from '../../typescript/pages';
import { getLocationOverviewRes, getLocationRes, getTaxonomyRes } from '../../helper';
import { SEO } from '../../helper/seo';
import { GetServerSidePropsContext } from 'next';
import { setPageCacheHeaders } from '../../helper/cache-control';
import Link from 'next/link';
import { mapStyle } from '../../typescript/mapStyle';
import { getMapIcon, getGoogleMapsLoader } from '../../sdk/utils';
import { useRouter } from 'next/router';
import { AnimatePresence, motion } from 'framer-motion';
import { normLng, parseCoord } from '../../helper/mapHelper';
import { translate } from '../../helper/translate';

type Props = {
  page: LocationsOverviewPage;
  locations: LocationRes[];
  locationFilter: TaxonomyRes[];
  certificationFilter: TaxonomyRes[];
  locale: string;
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const locale = context.locale || context.defaultLocale || 'en-us';
    const [page, locations, locationFilter, certificationFilter] = await Promise.all([
      getLocationOverviewRes('/locations', locale),
      getLocationRes(locale),
      getTaxonomyRes('location'),
      getTaxonomyRes('certifications'),
    ]);

    if (!page) {
      return {
        notFound: true,
      };
    }

    setPageCacheHeaders(context.res);

    return {
      props: {
        page,
        locations: locations?.flat?.() || [],
        locationFilter: Array.isArray(locationFilter) ? locationFilter : [],
        certificationFilter: Array.isArray(certificationFilter) ? certificationFilter : [],

        locale,
      },
    };
  } catch (error) {
    console.error('Error loading Locations Overview:', error);
    return { notFound: true };
  }
}

export default function LocationsOverview({
  page,
  locations,
  locationFilter,
  certificationFilter,
  locale,
}: Props) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const [mapReady, setMapReady] = useState(false); // NEW
  const router = useRouter();
  const queryAppliedRef = useRef(false);

  const [initialSelectedFilters, setInitialSelectedFilters] = useState<{
    locations: string[];
    certifications: string[];
    metals: string[];
  } | null>(null);

  const [selectedFilters, setSelectedFilters] = useState<{
    locations: string[];
    certifications: string[];
    metals: string[];
  }>({
    locations: [],
    certifications: [],
    metals: [],
  });

  // Pre-filter taxonomies only if in use
  const locationUidsInUse = new Set<string>();
  const certificationUidsInUse = new Set<string>();
  locations.forEach(loc => {
    loc.taxonomies?.forEach(tax => {
      if (tax.taxonomy_uid === 'location') locationUidsInUse.add(tax.term_uid);
      if (tax.taxonomy_uid === 'certifications') certificationUidsInUse.add(tax.term_uid);
    });
  });

  const filteredLocationFilter = Array.isArray(locationFilter)
    ? locationFilter.filter(loc => {
        const hasMatchingChild = locationFilter.some(
          child => child.parent_uid === loc.uid && locationUidsInUse.has(child.uid),
        );
        return locationUidsInUse.has(loc.uid) || hasMatchingChild;
      })
    : [];

  const filteredCertificationFilter = Array.isArray(certificationFilter)
    ? certificationFilter.filter(loc => {
        const hasMatchingChild = locationFilter.some(
          child => child.parent_uid === loc.uid && certificationUidsInUse.has(child.uid),
        );
        return certificationUidsInUse.has(loc.uid) || hasMatchingChild;
      })
    : [];

  const [filters] = useState({
    locations: filteredLocationFilter,
    certifications: filteredCertificationFilter,
    metals: [],
  });
  const [filteredLocations, setFilteredLocations] = useState<LocationRes[]>(locations);

  // Load map on first render, and rebuild it if the locale (map language) changes.
  useEffect(() => {
    let cancelled = false;

    // Tear down any existing map/markers before a (re)build so stale objects from
    // a previous language don't linger. Guarded because the old google instance
    // may be about to be replaced.
    markersRef.current.forEach(m => {
      try {
        m.setMap(null);
      } catch {
        /* stale marker from a previous map language */
      }
    });
    markersRef.current = [];
    setMapReady(false);

    const loader = getGoogleMapsLoader('AIzaSyAH8sRFq8E04g-lGKsvFvUCczd5n7pDrBA', locale);

    const loadMap = async () => {
      const { Map } = (await loader.importLibrary('maps')) as google.maps.MapsLibrary;

      if (cancelled || !mapRef.current) return;

      const screenWidth = window.innerWidth;
      let zoom = 1.97; // Default for desktop

      if (screenWidth < 768) {
        zoom = 1.3; // Mobile
      } else if (screenWidth < 1024) {
        zoom = 1.5; // Tablet
      }

      const map = new Map(mapRef.current, {
        center: { lat: 35, lng: -350 },
        zoom,
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        zoomControl: true,
        gestureHandling: 'none',
        keyboardShortcuts: true,
        draggable: true,
        scrollwheel: true,
        disableDoubleClickZoom: false,
        styles: mapStyle,
        tilt: 0,
        heading: 0,
      });

      map.addListener('mouseover', () => map.setOptions({ gestureHandling: 'greedy' }));
      map.addListener('mouseout', () => map.setOptions({ gestureHandling: 'none' }));

      mapInstance.current = map;
      setMapReady(true);
    };

    loadMap();

    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    if (!mapInstance.current || !mapReady) return;

    // clear
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    const list = filteredLocations.filter(
      l =>
        l.latitude &&
        l.longitude &&
        !Number.isNaN(parseCoord(l.latitude)!) &&
        !Number.isNaN(normLng(parseCoord(l.longitude)!)),
    );
    if (!list.length) return;

    const bounds = new google.maps.LatLngBounds();

    // build a safe absolute URL for your custom pin
    const iconUrl = (() => {
      try {
        return new URL(getMapIcon(), window.location.origin).toString();
      } catch {
        return getMapIcon();
      }
    })();

    list.forEach(loc => {
      const pos = { lat: parseCoord(loc.latitude)!, lng: normLng(parseCoord(loc.longitude)!) };
      bounds.extend(pos);

      const marker = new google.maps.Marker({
        position: pos,
        map: mapInstance.current!,
        title: loc.header_title || 'Location',
        icon: {
          url: iconUrl, // falls back to default pin if this 404s
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 32),
        },
        animation: google.maps.Animation.DROP,
      });

      marker.addListener('click', () => {
        if (loc.url) router.push(`/${locale}${loc.url}`);
      });
      marker.addListener('mouseover', () => marker.setAnimation(google.maps.Animation.BOUNCE));
      marker.addListener('mouseout', () => marker.setAnimation(null));

      markersRef.current.push(marker);
    });
  }, [filteredLocations, locale, mapReady]);

  const continents = filters.locations?.filter(loc => loc.depth === 1) ?? [];
  const countries = filters.locations?.filter(loc => loc.depth === 2) ?? [];

  const matchesSelectedFilters = (
    loc: LocationRes,
    selected: { locations: string[]; certifications: string[]; metals: string[] },
  ) => {
    const locationMatch =
      selected.locations.length === 0 ||
      loc.taxonomies.some(
        tax => tax.taxonomy_uid === 'location' && selected.locations.includes(tax.term_uid),
      );

    // Old logic based on certifications array
    // const certificationMatch =
    //   selected.certifications.length === 0 ||
    //   loc.certifications?.some(certGroup =>
    //     certGroup.certification.some(cert =>
    //       cert.taxonomies?.some(
    //         t =>
    //           t.taxonomy_uid === 'certifications' && selected.certifications.includes(t.term_uid),
    //       ),
    //     ),
    //   );

    // New logic based on taxonomies array
    const certificationMatch =
      selected.certifications.length === 0 ||
      loc.taxonomies?.some(tax => 
        selected.certifications.includes(tax.term_uid)
      );

    return locationMatch && certificationMatch;
  };

  const applyFilters = (
    selected: { locations: string[]; certifications: string[]; metals: string[] },
    updateUrl = true,
  ) => {
    setSelectedFilters(selected);

    const noFiltersSelected =
      selected.locations.length === 0 && selected.certifications.length === 0;

    if (noFiltersSelected) {
      setFilteredLocations(locations);
    } else {
      const filtered = locations.filter(loc => matchesSelectedFilters(loc, selected));
      setFilteredLocations(filtered);
    }

    if (updateUrl) {
      const certNames = selected.certifications
        .map(uid => certificationFilter.find(c => c.uid === uid)?.name)
        .filter(Boolean) as string[];
      const certParam = encodeURIComponent(certNames.join(','));
      const currentCertParam = router.query.cert ? String(router.query.cert) : '';
      const currentEncoded = encodeURIComponent(currentCertParam);

      const locParam = selected.locations.join(',');
      const currentLocParam = router.query.loc ? String(router.query.loc) : '';

      if (certParam === currentEncoded && locParam === currentLocParam) {
        return;
      }

      const query: Record<string, string> = {};
      if (certParam) {
        query.cert = certParam;
      }
      if (locParam) {
        query.loc = locParam;
      }

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    }
  };

  const findLocationsForCountry = (countryUid: string) =>
    filteredLocations.filter(loc =>
      loc.taxonomies?.some(tax => tax.taxonomy_uid === 'location' && tax.term_uid === countryUid),
    );

  const pageTitleData: PageTitleHeader = {
    title: page?.header_title,
    description: page?.header_description,
  };

  useEffect(() => {
    if (!router.isReady || queryAppliedRef.current) return;

    const { cert, loc } = router.query;

    let selected: {
      locations: string[];
      certifications: string[];
      metals: string[];
    } = {
      locations: [],
      certifications: [],
      metals: [],
    };

    // Handle certification filter
    if (typeof cert === 'string') {
      const decoded = decodeURIComponent(cert)
        .split(',')
        .map(name => name.trim().toLowerCase());

      const matchedCerts = certificationFilter.filter(cert =>
        decoded.includes(cert.name.toLowerCase()),
      );
      selected.certifications = matchedCerts.map(c => c.uid);
    }

    // Handle location filter
    if (typeof loc === 'string') {
      const locationUids = loc.split(',').map(uid => uid.trim());
      selected.locations = locationUids;
    }

    // Apply filters if any are present
    if (selected.certifications.length > 0 || selected.locations.length > 0) {
      queryAppliedRef.current = true;

      setInitialSelectedFilters(selected);
      setSelectedFilters(selected);
      applyFilters(selected, false);
    } else {
      // Reset filters if no querystring parameters
      queryAppliedRef.current = true;
      setInitialSelectedFilters(null);
      setSelectedFilters({ locations: [], certifications: [], metals: [] });
      setFilteredLocations(locations);
    }
  }, [router.isReady, router.query.cert, router.query.loc, certificationFilter, locationFilter]);

  // Ensure URL is maintained when filters change
  useEffect(() => {
    if (!router.isReady) return;

    const currentCert = router.query.cert ? String(router.query.cert) : '';
    const currentLoc = router.query.loc ? String(router.query.loc) : '';

    const expectedCert =
      selectedFilters.certifications.length > 0
        ? encodeURIComponent(
            selectedFilters.certifications
              .map(uid => certificationFilter.find(c => c.uid === uid)?.name)
              .filter(Boolean)
              .join(','),
          )
        : '';
    const expectedLoc = selectedFilters.locations.join(',');

    if (currentCert !== expectedCert || currentLoc !== expectedLoc) {
      const query: Record<string, string> = {};
      if (expectedCert) query.cert = expectedCert;
      if (expectedLoc) query.loc = expectedLoc;

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [selectedFilters, router.isReady, certificationFilter]);

  const meta_title = page?.seo?.meta_title || 'Default Title';
  const meta_description = page?.seo?.meta_description || 'Default description';
  const keywords = page?.seo?.keywords || 'default, keywords';

  return (
    <>
      <SEO
        meta_title={meta_title}
        meta_description={meta_description}
        keywords={keywords}
        share_image={''}
        contentType="Location"
        currentLocale={locale}
        baseUrl={process.env.NEXT_PUBLIC_HOSTED_URL || ''}
        authorName={'Form Technologies'}
      />
      <div className={styles.outerWrapper}>
        <PageTitle key="contactPageTitle" page_title={pageTitleData} />
        <div className={styles.moduleWrapper}>
          <Filter
            key="locationsFilter"
            filter={filters}
            onApplyFilters={applyFilters}
            defaultSelected={initialSelectedFilters || selectedFilters}
          />
          <div className={styles.frameParent}>
            <section className={styles.rectangleParent}>
              <div
                ref={mapRef}
                style={{ width: '100%', height: '28rem', borderRadius: '8px' }}
              ></div>
            </section>
            <AnimatePresence>
              <motion.div
                key={filteredLocations.map(l => l.uid).join('-')} // force remount on filter
                className={styles.resultsWrapper}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {continents
                  .filter(continent =>
                    countries.some(
                      country =>
                        country.parent_uid === continent.uid &&
                        findLocationsForCountry(country.uid).length > 0,
                    ),
                  )
                  .map((continent, continentIndex) => (
                    <motion.section
                      key={continent.uid}
                      className={styles.resultsGroup}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 15 }}
                      transition={{ duration: 0.2, delay: continentIndex * 0.1 }}
                    >
                      <motion.div
                        className={styles.continent}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {translate(continent.name.toLowerCase(), locale, continent.name)}
                      </motion.div>

                      {countries
                        .filter(country => country.parent_uid === continent.uid)
                        .map((country, countryIndex) => {
                          const countryLocations = findLocationsForCountry(country.uid);
                          return countryLocations.length ? (
                            <motion.div
                              key={country.uid}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 10 }}
                              transition={{ duration: 0.2, delay: countryIndex * 0.05 }}
                            >
                              <div className={styles.country}>
                                {translate(country.name.toLowerCase(), locale, country.name)}
                              </div>

                              <AnimatePresence>
                                {countryLocations.map((location, index) => (
                                  <motion.div
                                    key={location.uid}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                  >
                                    <Link
                                      href={location.url}
                                      locale={locale}
                                      className={styles.location}
                                    >
                                      {location.header_title}
                                    </Link>
                                  </motion.div>
                                ))}
                              </AnimatePresence>
                            </motion.div>
                          ) : null;
                        })}
                    </motion.section>
                  ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
}
